import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type BankAccountResponse,
  CARD_KIND_LABELS,
  CARD_KINDS,
  CARD_STATE_LABELS,
  CARD_STATES,
  cardCategoryCode,
  type CardDebitRow,
  type CardDebitsResponse,
  type CardDebitState,
  cardMonthLabel,
  type CardKind,
  type CardLoadResponse,
  type CardLoadResult,
  type CardMovementResponse,
  type CardMovementsResponse,
  type CardRequest,
  cardSchema,
  type CardSpendingResponse,
  type CardState,
  type CardSummary,
  formatMoney,
  fromCents,
  italianToday,
  SPENDING_GROUP_LABELS,
  SPENDING_GROUPS,
  type SpendingGroup,
  toCents,
} from '@k-management/shared';
import { type ChangeEvent, type FormEvent, Fragment, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { CsvExportDialog } from './CsvExportDialog.js';
import { StartWorkDialog } from './StartWorkDialog.js';
import { movementPath, worklistKeys } from './treasury-ui.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { usePreference } from '../../lib/preferences.js';
import { moneyOf } from '../people/people-ui.js';
import {
  AccountingCell,
  AccountingDialog,
  accountingState,
  type AccountingState,
  ACCOUNTING_STATE_LABELS,
  ConfirmProposals,
  confirmableProposals,
  proposesInvoice,
  RulesTab,
  ShopPanel,
  ShopRuleCell,
  useAccountingOptions,
} from './CardAccounting.js';
import { useListFilters } from '../../lib/list-filters.js';

const cardKeys = {
  all: ['treasury', 'cards'] as const,
  loads: ['treasury', 'cards', 'loads'] as const,
  movements: (filters: object) => ['treasury', 'cards', 'movements', filters] as const,
  spending: (year: number, by: SpendingGroup) =>
    ['treasury', 'cards', 'spending', year, by] as const,
  debits: ['treasury', 'cards', 'debits'] as const,
};

type Tab = 'carte' | 'spese' | 'addebiti' | 'controllo' | 'regole';
const TABS: Record<Tab, string> = {
  carte: 'Carte',
  spese: 'Spese',
  addebiti: 'Addebiti mensili',
  controllo: 'Controllo spese',
  regole: 'Regole',
};

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const monthName = (month: number) =>
  new Date(2000, month - 1, 15).toLocaleDateString('it-IT', { month: 'long' });

/**
 * Tesoreria › Carte di credito (10/10/2026): the cards of K-Rev, their
 * spending loaded from the files of the bank, and the spending by month to
 * keep the costs under control.
 */
export function CardsPage() {
  usePageTitle('Carte di credito');
  const canWrite = useCan('tesoreria.write');
  const [tab, setTab] = usePreference<Tab>('treasury.cards.tab', 'carte');
  // A spending opened from the page of an invoice (1.85): its tab, month and card.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('spesa');
  const focus = focusId
    ? { id: focusId, date: searchParams.get('data') ?? '', cardId: searchParams.get('carta') }
    : null;
  useEffect(() => {
    if (focus && tab !== 'spese') setTab('spese');
  }, [focusId]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<CardSummary | 'new' | null>(null);
  const [exporting, setExporting] = useState(false);
  const cards = useQuery({
    queryKey: cardKeys.all,
    queryFn: () => apiRequest<CardSummary[]>('/treasury/cards'),
  });
  useCommands([
    ...(canWrite
      ? [
          {
            key: 'load',
            label: 'Carica file della banca',
            icon: 'upload',
            primary: true,
            onClick: () => setLoading(true),
          } satisfies Command,
          {
            key: 'new',
            label: 'Nuova carta',
            icon: 'plus',
            onClick: () => setEditing('new'),
          } satisfies Command,
        ]
      : []),
    {
      key: 'csv',
      label: 'Esporta CSV',
      icon: 'down',
      onClick: () => setExporting(true),
    } satisfies Command,
  ]);

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Carte di credito</h1>
          <p className="muted">
            Le carte e le loro spese, dai file del portale Intesa («situazioneCarte» e
            «listaMovimenti»). Delle carte si conservano solo le ultime 4 cifre. Le spese delle
            carte di credito la banca le addebita tutte insieme il 28 del mese dopo.
          </p>
        </div>
      </div>
      <div className="segmented-small cards-tabs" role="tablist" aria-label="Sezione">
        {(Object.keys(TABS) as Tab[]).map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'selected' : ''}
            onClick={() => setTab(item)}
          >
            {TABS[item]}
          </button>
        ))}
      </div>
      {cards.isError && <p className="error">{cards.error.message}</p>}
      {tab === 'carte' && cards.data && (
        <CardsTab cards={cards.data} canWrite={canWrite} onOpen={setEditing} />
      )}
      {tab === 'spese' && cards.data && (
        <SpendingTab
          cards={cards.data}
          canWrite={canWrite}
          focus={focus}
          onFocused={() => setSearchParams({}, { replace: true })}
        />
      )}
      {tab === 'addebiti' && <DebitsTab />}
      {tab === 'controllo' && <ControlTab canWrite={canWrite} />}
      {tab === 'regole' && <RulesTab canWrite={canWrite} />}
      {loading && <LoadDialog onClose={() => setLoading(false)} />}
      {exporting && <CardCsvExport cards={cards.data ?? []} onClose={() => setExporting(false)} />}
      {editing && (
        <CardDialog
          card={editing === 'new' ? undefined : editing}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function CardsTab({
  cards,
  canWrite,
  onOpen,
}: {
  cards: CardSummary[];
  canWrite: boolean;
  onOpen: (card: CardSummary) => void;
}) {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<CardLoadResponse | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const loads = useQuery({
    queryKey: cardKeys.loads,
    queryFn: () => apiRequest<CardLoadResponse[]>('/treasury/cards/loads'),
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/treasury/cards/loads/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['treasury', 'cards'] });
      setRemoving(null);
    },
  });
  const open = cards.filter((card) => card.state !== 'chiusa');
  const shown = showClosed ? cards : open;
  const sum = (pick: (card: CardSummary) => string) =>
    fromCents(cards.reduce((total, card) => total + toCents(pick(card)), 0));
  const month = monthName(Number(italianToday().slice(5, 7)));

  return (
    <>
      <div className="treasury-tiles">
        <div className="treasury-tile">
          <span>Spese di {month}</span>
          <strong>{formatMoney(sum((card) => card.spentThisMonth))}</strong>
        </div>
        <div className="treasury-tile">
          <span>Spese dell’anno</span>
          <strong>{formatMoney(sum((card) => card.spentThisYear))}</strong>
        </div>
        <div className="treasury-tile">
          <span>Carte non chiuse</span>
          <strong>{open.length}</strong>
        </div>
      </div>
      {cards.length === 0 && (
        <p className="notice info">
          Nessuna carta
          {canWrite
            ? ': carica «situazioneCarte» e i «listaMovimenti» con «Carica file della banca».'
            : '.'}
        </p>
      )}
      {cards.length > open.length && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(event) => setShowClosed(event.target.checked)}
          />
          Anche le carte chiuse ({cards.length - open.length})
        </label>
      )}
      <div className="loan-cards">
        {shown.map((card) => {
          const limit = card.spendingLimit ? toCents(card.spendingLimit) : null;
          const used =
            limit && card.kind === 'credito'
              ? Math.min(100, Math.max(0, Math.round((toCents(card.spentThisMonth) / limit) * 100)))
              : null;
          return (
            <button
              key={card.id}
              type="button"
              className="card loan-card card-tile"
              onClick={() => onOpen(card)}
            >
              <strong className="mono">•••• {card.last4}</strong>{' '}
              <span className={`pill ${card.kind === 'credito' ? 'brand' : 'muted'}`}>
                {CARD_KIND_LABELS[card.kind]}
              </span>{' '}
              {card.state !== 'attiva' && (
                <span className={`pill ${card.state === 'chiusa' ? 'muted' : 'warn'}`}>
                  {CARD_STATE_LABELS[card.state]}
                </span>
              )}
              <span className="block">{card.holder}</span>
              {used !== null && (
                <div className="loan-bar" title={`${used}% del limite usato nel mese`}>
                  <i style={{ width: `${used}%` }} />
                </div>
              )}
              <small className="muted block">
                {month}: {formatMoney(card.spentThisMonth)}
                {card.spendingLimit && ` su ${formatMoney(card.spendingLimit)}`}
              </small>
              <small className="muted block">
                {card.movements === 0
                  ? card.kind === 'debito'
                    ? 'spese nei movimenti della banca'
                    : 'nessuna spesa caricata'
                  : `${card.movements} spese · ultima ${formatDate(card.lastMovement!)}`}
              </small>
            </button>
          );
        })}
      </div>
      {loads.data && loads.data.length > 0 && (
        <details className="card loads-card">
          <summary>
            <strong>File caricati</strong> <span className="muted">({loads.data.length})</span>
          </summary>
          <ul className="plain-list">
            {loads.data.map((load) => (
              <li key={load.id} className="row between">
                <span>
                  {load.fileName}
                  <small className="muted block">
                    {load.periodFrom && load.periodTo
                      ? `${formatDate(load.periodFrom)} – ${formatDate(load.periodTo)} · `
                      : ''}
                    {load.created} spese nuove · {formatTimestamp(load.loadedAt)}
                    {load.loadedBy ? ` · ${load.loadedBy}` : ''}
                  </small>
                </span>
                {canWrite && (
                  <button type="button" className="link" onClick={() => setRemoving(load)}>
                    Togli
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
      {removing && (
        <ConfirmDialog
          title="Togliere il file?"
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate(removing.id)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            Si tolgono le {removing.created} spese caricate da «{removing.fileName}». Le carte
            restano.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

function SpendingTab({
  cards,
  canWrite,
  focus,
  onFocused,
}: {
  cards: CardSummary[];
  canWrite: boolean;
  /** A spending to open, with its day and card: the filters go to it. */
  focus: { id: string; date: string; cardId: string | null } | null;
  onFocused: () => void;
}) {
  const today = italianToday();
  // «Con una proposta di fattura»: to account for or with only its item (2.24).
  const [state, setState] = usePreference<'all' | AccountingState | 'proposta-fattura'>(
    'treasury.cards.state',
    'all',
  );
  const [opened, setOpened] = useState<CardMovementResponse | null>(null);
  const [confirming, setConfirming] = useState(false);
  const options = useAccountingOptions();
  // The filters stay while the browser is open: found again coming back to the list (1.61).
  const list = useListFilters('card-movements', {
    year: Number(today.slice(0, 4)) as number,
    month: Number(today.slice(5, 7)) as number | 'all',
    cardId: 'all' as string,
    category: 'all' as string,
  });
  const { year, month, cardId, category } = list.filters;
  const { setFilter, reset, changed, changedClass } = list;
  const setYear = (value: number) => setFilter('year', value);
  const setMonth = (value: number | 'all') => setFilter('month', value);
  const setCardId = (value: string) => setFilter('cardId', value);
  const setCategory = (value: string) => setFilter('category', value);
  const filters = { year, month, cardId };
  const data = useQuery({
    queryKey: cardKeys.movements(filters),
    queryFn: () => {
      const query = new URLSearchParams({ year: String(year) });
      if (month !== 'all') query.set('month', String(month));
      if (cardId !== 'all') query.set('cardId', cardId);
      return apiRequest<CardMovementsResponse>(`/treasury/cards/movements?${query.toString()}`);
    },
  });
  const items = data.data?.items ?? [];
  // The spending asked for (1.85): its month and card, then it opens.
  useEffect(() => {
    if (!focus || !/^\d{4}-\d{2}-\d{2}$/.test(focus.date)) return;
    const [focusYear, focusMonth] = focus.date.split('-').map(Number);
    if (year !== focusYear) setYear(focusYear!);
    if (month !== focusMonth) setMonth(focusMonth!);
    if (focus.cardId && cardId !== focus.cardId) setCardId(focus.cardId);
  }, [focus?.id]);
  useEffect(() => {
    if (!focus || data.isFetching) return;
    const found = items.find((item) => item.id === focus.id);
    if (found) {
      setOpened(found);
      onFocused();
    }
  }, [focus?.id, data.data, data.isFetching]);
  const categories = new Map<string, string>();
  for (const item of items) {
    const code = cardCategoryCode(item.mcc);
    if (code) categories.set(code, item.category ?? `Codice ${item.mcc}`);
  }
  const inCategory =
    category === 'all' ? items : items.filter((item) => cardCategoryCode(item.mcc) === category);
  const rows =
    state === 'all'
      ? inCategory
      : state === 'proposta-fattura'
        ? inCategory.filter(proposesInvoice)
        : inCategory.filter((item) => accountingState(item) === state);
  const invoiceProposals = inCategory.filter(proposesInvoice).length;
  const counts = inCategory.reduce(
    (total, item) => {
      total[accountingState(item)] += 1;
      return total;
    },
    {
      fattura: 0,
      soggetto: 0,
      conto: 0,
      'da-collegare': 0,
      proposta: 0,
      'da-rendicontare': 0,
    } as Record<AccountingState, number>,
  );
  const proposed = confirmableProposals(rows);
  const total = fromCents(rows.reduce((sum, item) => sum + toCents(item.amount), 0));
  const years = [...new Set([...(data.data?.years ?? []), Number(today.slice(0, 4))])].sort(
    (a, b) => b - a,
  );
  const columns: GridColumn<CardMovementResponse>[] = [
    {
      key: 'date',
      label: 'Data',
      render: (item) => formatDate(item.date),
      value: (item) => item.date,
      required: true,
    },
    {
      key: 'card',
      label: 'Carta',
      render: (item) => (
        <>
          <span className="mono">•••• {item.last4}</span>
          <small className="muted block">{item.holder}</small>
        </>
      ),
      value: (item) => `${item.last4} ${item.holder}`,
    },
    {
      key: 'description',
      label: 'Descrizione della banca',
      render: (item) => item.description,
      value: (item) => item.description,
      required: true,
    },
    {
      key: 'category',
      label: 'Categoria',
      render: (item) => item.category ?? (item.mcc ? `Codice ${item.mcc}` : '—'),
      value: (item) => item.category ?? '',
    },
    {
      key: 'original',
      label: 'Importo originale',
      // Room for the rendicontazione: shown with «Colonne» (the dialog has it too).
      hiddenByDefault: true,
      align: 'right',
      render: (item) =>
        item.currency === 'EUR' ? (
          ''
        ) : (
          <>
            {formatMoney(item.originalAmount).replace('€', '').trim()} {item.currency}
            {item.exchangeRate && (
              <small className="muted block">
                cambio {item.exchangeRate.replace('.', ',').replace(/0+$/, '')}
              </small>
            )}
          </>
        ),
      value: (item) => (item.currency === 'EUR' ? null : Number(item.originalAmount)),
    },
    {
      key: 'amount',
      label: 'Euro',
      align: 'right',
      render: (item) => formatMoney(item.amount),
      value: (item) => Number(item.amount),
      required: true,
    },
    {
      key: 'accounting',
      label: 'Rendicontazione',
      render: (item) => (
        <div className="accounting-cell">
          <AccountingCell item={item} items={options.data?.items} />
        </div>
      ),
      value: (item) => ACCOUNTING_STATE_LABELS[accountingState(item)],
    },
  ];

  return (
    <>
      <div className="treasury-tiles">
        <div className="treasury-tile">
          <span>
            Spese {month === 'all' ? `del ${year}` : `di ${monthName(month)} ${year}`}
            {cardId !== 'all' || category !== 'all' ? ' (filtrate)' : ''}
          </span>
          <strong>{formatMoney(total)}</strong>
        </div>
        <div className="treasury-tile">
          <span>Numero di spese</span>
          <strong>{rows.length}</strong>
        </div>
        <div className="treasury-tile">
          <span>Da rendicontare</span>
          <strong>{counts.proposta + counts['da-rendicontare']}</strong>
          <small className="muted">
            {counts.proposta} con una proposta · {invoiceProposals} di fattura
          </small>
        </div>
        <div className="treasury-tile">
          <span>Rendicontate</span>
          <strong>
            {counts.fattura + counts.soggetto + counts.conto + counts['da-collegare']}
          </strong>
          <small className="muted">
            {counts.fattura} con fattura · {counts.conto} con conto
            {counts.soggetto > 0 && ` · ${counts.soggetto} al soggetto`}
            {counts['da-collegare'] > 0 && ` · ${counts['da-collegare']} fattura da collegare`}
          </small>
        </div>
      </div>
      {canWrite && proposed.length > 0 && (
        <div className="row">
          <button type="button" className="primary" onClick={() => setConfirming(true)}>
            Conferma le proposte dell’elenco ({proposed.length})
          </button>
          <small className="muted">
            Prima si possono aprire e cambiare una per una. Le proposte di fattura anche prima del
            2025 (chiudono la fattura), quelle di conto da gennaio 2025.
          </small>
        </div>
      )}
      {data.isError && <p className="error">{data.error.message}</p>}
      <DataGrid
        id="card-movements"
        filtersChanged={changed}
        onResetFilters={reset}
        columns={columns}
        rows={rows}
        rowKey={(item) => item.id}
        onRowOpen={setOpened}
        openOnClick
        searchPlaceholder="Descrizione, carta o importo"
        empty={data.isPending ? 'Caricamento…' : 'Nessuna spesa con questi filtri.'}
        toolbar={
          <>
            <select
              className={changedClass('year')}
              aria-label="Anno"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
            >
              {years.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <select
              className={changedClass('month')}
              aria-label="Mese"
              value={month}
              onChange={(event) =>
                setMonth(event.target.value === 'all' ? 'all' : Number(event.target.value))
              }
            >
              <option value="all">Tutti i mesi</option>
              {MONTHS.map((_, index) => (
                <option key={index} value={index + 1}>
                  {monthName(index + 1)}
                </option>
              ))}
            </select>
            <select
              className={changedClass('cardId')}
              aria-label="Carta"
              value={cardId}
              onChange={(event) => setCardId(event.target.value)}
            >
              <option value="all">Tutte le carte</option>
              {cards.map((card) => (
                <option key={card.id} value={card.id}>
                  •••• {card.last4} · {card.holder}
                </option>
              ))}
            </select>
            <select
              className={changedClass('category')}
              aria-label="Categoria"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="all">Tutte le categorie</option>

              {[...categories.entries()]
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
            </select>
            <select
              className="compact-select"
              aria-label="Rendicontazione"
              value={state}
              onChange={(event) =>
                setState(event.target.value as 'all' | AccountingState | 'proposta-fattura')
              }
            >
              <option value="all">Rendicontazione: tutte</option>
              <option value="proposta-fattura">
                Con una proposta di fattura ({invoiceProposals})
              </option>
              {(Object.keys(ACCOUNTING_STATE_LABELS) as AccountingState[]).map((item) => (
                <option key={item} value={item}>
                  {ACCOUNTING_STATE_LABELS[item]} ({counts[item]})
                </option>
              ))}
            </select>
          </>
        }
      />
      {opened && (
        <AccountingDialog item={opened} canWrite={canWrite} onClose={() => setOpened(null)} />
      )}
      {confirming && <ConfirmProposals rows={proposed} onClose={() => setConfirming(false)} />}
    </>
  );
}

const DEBIT_STATE_PILLS: Record<CardDebitState, string> = {
  quadra: 'pill ok',
  passa: 'pill ok',
  sospeso: 'pill warn',
  'da-verificare': 'pill bad',
  atteso: 'pill muted',
  'non-trovato': 'pill bad',
};

/** The state of a month in words; `after` is the month after it. */
function debitState(row: CardDebitRow, after: CardDebitRow | undefined): string {
  const pending = row.pending ? formatMoney(row.pending) : '';
  switch (row.state) {
    case 'quadra':
      return 'quadra';
    case 'passa':
      return `${pending} passano a ${after ? cardMonthLabel(after.month).split(' ')[0] : 'dopo'}`;
    case 'sospeso':
      return `${pending} in sospeso`;
    case 'da-verificare':
      return row.pending && toCents(row.pending) < 0
        ? `da verificare: addebitati ${formatMoney(fromCents(-toCents(row.pending)))} in più`
        : `da verificare: ${pending} non addebitati`;
    case 'atteso':
      return `da addebitare il ${formatDate(row.dueDate)}`;
    case 'non-trovato':
      return 'addebito non trovato';
  }
}

/**
 * The monthly debits of the bank against the spending of the credit cards
 * (1.54): month by month, what is in suspense and whether it squares.
 */
function DebitsTab() {
  const [open, setOpen] = useState<string | null>(null);
  const data = useQuery({
    queryKey: cardKeys.debits,
    queryFn: () => apiRequest<CardDebitsResponse>('/treasury/cards/debits'),
  });
  if (data.isError) return <p className="error">{data.error.message}</p>;
  if (!data.data) return <p className="muted">Caricamento…</p>;
  const { rows, next, carryOver } = data.data;
  const spending = new Map(data.data.spending.map((item) => [item.id, item]));
  const last = rows.find((row) => row.debit !== null);
  const toCheck = rows.filter(
    (row) => row.state === 'da-verificare' || row.state === 'non-trovato',
  );

  return (
    <>
      <div className="treasury-tiles">
        <div className="treasury-tile">
          <span>Prossimo addebito previsto</span>
          <strong>
            {next ? `${formatDate(next.dueDate)} · ~ ${formatMoney(next.amount)}` : '—'}
          </strong>
          {next && <small className="muted">spese di {cardMonthLabel(next.month)}</small>}
        </div>
        <div className="treasury-tile">
          <span>Ultimo addebito</span>
          <strong>
            {last ? `${formatDate(last.debits.at(-1)!.date)} · ${formatMoney(last.debit!)}` : '—'}
          </strong>
          {last && <small className="muted">spese di {cardMonthLabel(last.month)}</small>}
        </div>
        <div className="treasury-tile">
          <span>Spese in sospeso</span>
          <strong>{formatMoney(last?.pending ?? '0.00')}</strong>
        </div>
        <div className="treasury-tile">
          <span>Mesi da verificare</span>
          <strong>{toCheck.length}</strong>
        </div>
      </div>
      <p className="muted small">
        L’addebito Intesa «ADD. E/C CARTA» del 28 paga le spese con carta di credito del mese prima;
        le spese degli ultimi giorni che la banca registra dopo passano all’addebito successivo. Le
        carte di debito non ci sono: le loro spese sono già tra i movimenti della banca.
        {toCents(carryOver) !== 0 &&
          ` Il primo addebito comprende ${formatMoney(carryOver)} di spese precedenti al primo file caricato.`}
      </p>
      {rows.length === 0 ? (
        <p className="notice info">Nessuna spesa con carta di credito caricata.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table debits-table">
            <thead>
              <tr>
                <th>Spese di</th>
                <th className="numeric">Spese</th>
                <th>Addebito</th>
                <th className="numeric">Importo</th>
                <th className="numeric">Differenza</th>
                <th className="numeric">In sospeso</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const expanded = open === row.month;
                return (
                  <Fragment key={row.month}>
                    <tr className={expanded ? 'selected' : undefined}>
                      <td>
                        {row.lateSpending.length > 0 ? (
                          <button
                            type="button"
                            className="link"
                            aria-expanded={expanded}
                            onClick={() => setOpen(expanded ? null : row.month)}
                          >
                            {expanded ? '▾' : '▸'} {cardMonthLabel(row.month)}
                          </button>
                        ) : (
                          cardMonthLabel(row.month)
                        )}
                      </td>
                      <td className="numeric">{formatMoney(row.spending)}</td>
                      <td>
                        {row.debits.length
                          ? row.debits.map((item) => (
                              <Link
                                key={item.movementId}
                                className="block"
                                to={movementPath(item.accountCode, item.movementId)}
                              >
                                {formatDate(item.date)}
                              </Link>
                            ))
                          : `previsto ${formatDate(row.dueDate)}`}
                      </td>
                      <td className="numeric">{row.debit ? formatMoney(row.debit) : '—'}</td>
                      <td className="numeric">
                        {row.difference ? formatMoney(row.difference) : ''}
                      </td>
                      <td className="numeric">{row.pending ? formatMoney(row.pending) : ''}</td>
                      <td>
                        <span className={DEBIT_STATE_PILLS[row.state]}>
                          {debitState(row, rows[index - 1])}
                        </span>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={7} className="late-cell">
                          <strong>Spese degli ultimi giorni di {cardMonthLabel(row.month)}</strong>
                          <span className="muted">
                            {' '}
                            · {formatMoney(row.pending!)} passano all’addebito dopo
                            {row.probable.length > 0 &&
                              '; in evidenza le spese che fanno esattamente quella somma'}
                          </span>
                          <ul className="plain-list">
                            {row.lateSpending.map((id) => {
                              const item = spending.get(id);
                              if (!item) return null;
                              return (
                                <li key={id}>
                                  {formatDate(item.date)} ·{' '}
                                  <span className="mono">•••• {item.last4}</span> ·{' '}
                                  {item.description} · {formatMoney(item.amount)}
                                  {row.probable.includes(id) && (
                                    <>
                                      {' '}
                                      <span className="pill brand">probabile</span>
                                    </>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ControlTab({ canWrite }: { canWrite: boolean }) {
  const today = italianToday();
  const [shop, setShop] = useState<string | null>(null);
  const options = useAccountingOptions();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [by, setBy] = usePreference<SpendingGroup>('treasury.cards.by', 'categoria');
  const data = useQuery({
    queryKey: cardKeys.spending(year, by),
    queryFn: () =>
      apiRequest<CardSpendingResponse>(`/treasury/cards/spending?year=${year}&by=${by}`),
  });
  const summary = data.data;
  const shops = by === 'fornitore' ? summary?.shops : undefined;
  const years = [...new Set([...(summary?.years ?? []), Number(today.slice(0, 4))])].sort(
    (a, b) => b - a,
  );
  const months = summary ? Math.max(summary.lastMonth, 1) : 12;
  // Euro without the symbol: thirteen columns of amounts.
  const amount = (value: string) =>
    toCents(value) === 0 ? (
      <span className="muted">—</span>
    ) : (
      formatMoney(value).replace(/\s*€/, '')
    );

  return (
    <>
      <div className="row">
        <select
          className="compact-select"
          aria-label="Anno"
          value={year}
          onChange={(event) => setYear(Number(event.target.value))}
        >
          {years.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <div className="segmented-small" role="group" aria-label="Raggruppa per">
          {SPENDING_GROUPS.map((item) => (
            <button
              key={item}
              type="button"
              className={by === item ? 'selected' : ''}
              onClick={() => setBy(item)}
            >
              {SPENDING_GROUP_LABELS[item]}
            </button>
          ))}
        </div>
      </div>
      {data.isError && <p className="error">{data.error.message}</p>}
      {summary && (
        <>
          <p className="muted small">
            Spese con carta per mese in euro, rimborsi tolti.{' '}
            {summary.lastMonth > 0 &&
              `L’ultima colonna confronta con lo stesso periodo del ${year - 1} (gennaio – ${monthName(summary.lastMonth)}).`}
          </p>
          <div className="table-scroll">
            <table className="data-table spending-table">
              <thead>
                <tr>
                  <th>{SPENDING_GROUP_LABELS[by]}</th>
                  {shops && (
                    <>
                      <th>Classificato come</th>
                      <th className="numeric" title="Spese di tutti gli anni">
                        Da rendicontare
                      </th>
                    </>
                  )}
                  {MONTHS.slice(0, months).map((label) => (
                    <th key={label} className="numeric">
                      {label}
                    </th>
                  ))}
                  <th className="numeric">Totale {year}</th>
                  <th className="numeric" title={`Stesso periodo del ${year - 1}`}>
                    {months === 12 ? '' : `Gen–${MONTHS[months - 1]} `}
                    {year - 1}
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      {shops ? (
                        <button type="button" className="link" onClick={() => setShop(row.key)}>
                          {row.label}
                        </button>
                      ) : (
                        row.label
                      )}
                    </td>
                    {shops && (
                      <>
                        <td>
                          <ShopRuleCell
                            rule={shops[row.key]?.rule ?? null}
                            items={options.data?.items}
                          />
                        </td>
                        <td className="numeric">
                          {shops[row.key]?.open ? (
                            <span className="pill warn">{shops[row.key]!.open}</span>
                          ) : (
                            <span className="muted">0</span>
                          )}
                          {shops[row.key]?.toLink ? (
                            <small className="muted block">
                              {shops[row.key]!.toLink} fatture da collegare
                            </small>
                          ) : null}
                        </td>
                      </>
                    )}
                    {row.months.slice(0, months).map((value, index) => (
                      <td key={index} className="numeric">
                        {amount(value)}
                      </td>
                    ))}
                    <td className="numeric">
                      <strong>{amount(row.total)}</strong>
                    </td>
                    <td className="numeric muted">{amount(row.previous)}</td>
                  </tr>
                ))}
                {summary.rows.length === 0 && (
                  <tr>
                    <td colSpan={months + (shops ? 5 : 3)} className="muted">
                      Nessuna spesa nel {year}.
                    </td>
                  </tr>
                )}
              </tbody>
              {summary.rows.length > 0 && (
                <tfoot>
                  <tr>
                    <th>Totale</th>
                    {shops && (
                      <>
                        <th />
                        <th className="numeric">
                          {Object.values(shops).reduce((sum, item) => sum + item.open, 0)}
                        </th>
                      </>
                    )}
                    {summary.totals.months.slice(0, months).map((value, index) => (
                      <th key={index} className="numeric">
                        {amount(value)}
                      </th>
                    ))}
                    <th className="numeric">{amount(summary.totals.total)}</th>
                    <th className="numeric">{amount(summary.totals.previous)}</th>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {shops && (
            <p className="muted small">
              Cliccando un fornitore si classificano tutte le sue spese, di ogni anno: con un conto
              o con le fatture di un fornitore dell’anagrafica.
            </p>
          )}
        </>
      )}
      {shop && <ShopPanel shopKey={shop} canWrite={canWrite} onClose={() => setShop(null)} />}
    </>
  );
}

async function base64Of(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let index = 0; index < bytes.length; index += 8192) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  }
  return btoa(binary);
}

/** «Carica file della banca»: the files checked and counted, then loaded. */
function LoadDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [started, setStarted] = useState<string | null>(null);
  const [files, setFiles] = useState<{ fileName: string; excel: string }[] | null>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const send = (chosen: { fileName: string; excel: string }[], dryRun: boolean) =>
    apiRequest<CardLoadResult>('/treasury/cards/loads', {
      method: 'POST',
      body: { files: chosen, dryRun },
    });
  const preview = useMutation({
    mutationFn: (chosen: { fileName: string; excel: string }[]) => send(chosen, true),
  });
  const load = useMutation({
    mutationFn: () => send(files!, false),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['treasury', 'cards'] });
      await queryClient.invalidateQueries({ queryKey: worklistKeys.all });
      // What came is to settle now, or it is forgotten (3.00).
      if (data.loadId) setStarted(data.loadId);
      else onClose();
    },
  });
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = [...(event.target.files ?? [])];
    if (!chosen.length) return;
    const read = await Promise.all(
      chosen.map(async (item) => ({ fileName: item.name, excel: await base64Of(item) })),
    );
    setFiles(read);
    preview.mutate(read);
  };
  const result = preview.data;
  if (started) {
    return (
      <StartWorkDialog
        loadKey={`carte:${started}`}
        title="File delle carte caricato"
        onClose={onClose}
      />
    );
  }
  const changes = result ? result.created + result.newCards.length + result.updatedCards.length : 0;
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Carica file della banca</h2>
        <p className="muted small">
          Dal portale Intesa: «situazioneCarte» (le carte, i titolari, i limiti) e i
          «listaMovimenti» (le spese), anche più file insieme e periodi che si sovrappongono: le
          spese già presenti si riconoscono e si saltano.
        </p>
        <input type="file" accept=".xls,.xlsx" multiple onChange={(event) => void choose(event)} />
        {preview.isPending && <p className="muted">Controllo dei file…</p>}
        {preview.error && <p className="error">{preview.error.message}</p>}
        {result && (
          <>
            <dl className="totals">
              <div>
                <dt>Spese nuove</dt>
                <dd>{result.created}</dd>
              </div>
              <div>
                <dt>Già caricate (saltate)</dt>
                <dd>{result.skipped}</dd>
              </div>
              <div>
                <dt>Carte nuove</dt>
                <dd>
                  {result.newCards.length
                    ? result.newCards.map((last4) => `•••• ${last4}`).join(', ')
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>Carte aggiornate</dt>
                <dd>
                  {result.updatedCards.length
                    ? result.updatedCards.map((last4) => `•••• ${last4}`).join(', ')
                    : '—'}
                </dd>
              </div>
            </dl>
            {result.unknownCategories.length > 0 && (
              <p className="notice info small">
                Categorie senza descrizione in Tabelle › Categorie carte:{' '}
                {result.unknownCategories.join(', ')}.
              </p>
            )}
            <div className="table-scroll load-files">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Periodo</th>
                    <th className="numeric">Nuove</th>
                    <th className="numeric">Già presenti</th>
                  </tr>
                </thead>
                <tbody>
                  {result.files.map((item, index) => (
                    <tr key={`${item.fileName}-${index}`}>
                      <td>{item.fileName}</td>
                      <td>
                        {item.kind === 'carte'
                          ? 'elenco delle carte'
                          : item.from && item.to
                            ? `${formatDate(item.from)} – ${formatDate(item.to)}`
                            : '—'}
                      </td>
                      <td className="numeric">{item.kind === 'carte' ? '' : item.created}</td>
                      <td className="numeric">{item.kind === 'carte' ? '' : item.skipped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {load.error && <p className="error">{load.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={load.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!result || changes === 0 || load.isPending}
            onClick={() => load.mutate()}
          >
            {load.isPending ? 'Attendere…' : result && changes === 0 ? 'Niente di nuovo' : 'Carica'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

const EMPTY: CardRequest = {
  last4: '',
  holder: '',
  kind: 'credito',
  spendingLimit: null,
  state: 'attiva',
  accountCode: null,
  notes: null,
};

/** A card entered or changed by hand: holder, state, limit, account it is charged to. */
function CardDialog({
  card,
  canWrite,
  onClose,
}: {
  card?: CardSummary;
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState<CardRequest>(
    card
      ? {
          last4: card.last4,
          holder: card.holder,
          kind: card.kind,
          spendingLimit: card.spendingLimit,
          state: card.state,
          accountCode: card.accountCode,
          notes: card.notes,
        }
      : EMPTY,
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const set = <K extends keyof CardRequest>(key: K, value: CardRequest[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const text = (value: string) => value.trim() || null;
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: ['treasury', 'cards'] });
    onClose();
  };
  const save = useMutation({
    mutationFn: (body: CardRequest) =>
      apiRequest<CardSummary>(card ? `/treasury/cards/${card.id}` : '/treasury/cards', {
        method: card ? 'PATCH' : 'POST',
        body,
      }),
    onSuccess: done,
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  const remove = useMutation({
    mutationFn: () => apiRequest<void>(`/treasury/cards/${card!.id}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body: CardRequest = {
      ...draft,
      spendingLimit: draft.spendingLimit
        ? (moneyOf(draft.spendingLimit) ?? draft.spendingLimit)
        : null,
    };
    const result = validate(cardSchema, body);
    setErrors(result.errors);
    if (Object.keys(result.errors).length === 0) save.mutate(body);
    else setFormError(Object.values(result.errors)[0] ?? 'Controlla i dati');
  };
  const accounts = (banks.data ?? []).filter((bank) => bank.kind === 'Banca');

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <h2>{card ? `Carta •••• ${card.last4}` : 'Nuova carta'}</h2>
        <fieldset className="stack" disabled={!canWrite}>
          <div className="form-grid">
            <Field label="Ultime 4 cifre" error={errors.last4}>
              <input
                className="mono-input"
                inputMode="numeric"
                maxLength={4}
                value={draft.last4}
                onChange={(e) => set('last4', e.target.value.replace(/\D/g, ''))}
              />
            </Field>
            <Field label="Titolare" error={errors.holder}>
              <input value={draft.holder} onChange={(e) => set('holder', e.target.value)} />
            </Field>
            <Field label="Tipo">
              <select value={draft.kind} onChange={(e) => set('kind', e.target.value as CardKind)}>
                {CARD_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {CARD_KIND_LABELS[kind]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Stato">
              <select
                value={draft.state}
                onChange={(e) => set('state', e.target.value as CardState)}
              >
                {CARD_STATES.map((state) => (
                  <option key={state} value={state}>
                    {CARD_STATE_LABELS[state]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Limite di spesa mensile" error={errors.spendingLimit}>
              <DecimalInput
                value={draft.spendingLimit ?? ''}
                onChange={(value) => set('spendingLimit', value || null)}
                decimals={[2, 2]}
              />
            </Field>
            <Field label="Conto di addebito" error={errors.accountCode}>
              <select
                value={draft.accountCode ?? ''}
                onChange={(e) => set('accountCode', e.target.value || null)}
              >
                <option value="">—</option>
                {accounts.map((bank) => (
                  <option key={bank.code} value={bank.code}>
                    {bank.alias}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Note">
            <textarea
              value={draft.notes ?? ''}
              rows={2}
              onChange={(e) => set('notes', text(e.target.value))}
            />
          </Field>
        </fieldset>
        {card && card.movements > 0 && (
          <p className="muted small">
            {card.movements} spese caricate: la carta non si elimina, si mette «Chiusa».
          </p>
        )}
        {formError && <p className="error">{formError}</p>}
        {remove.error && <p className="error">{remove.error.message}</p>}
        <div className="actions">
          {canWrite && card && card.movements === 0 && (
            <button type="button" className="danger" onClick={() => setConfirmDelete(true)}>
              Elimina
            </button>
          )}
          <button type="button" onClick={onClose} disabled={save.isPending}>
            {canWrite ? 'Annulla' : 'Chiudi'}
          </button>
          {canWrite && (
            <button type="submit" className="primary" disabled={save.isPending}>
              {save.isPending ? 'Attendere…' : 'Salva'}
            </button>
          )}
        </div>
      </form>
      {confirmDelete && (
        <ConfirmDialog
          title="Eliminare la carta?"
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>La carta •••• {card!.last4} non ha spese: si elimina.</p>
        </ConfirmDialog>
      )}
    </dialog>
  );
}

/** The spending of a period in CSV for the accountant (2.30): every card in one file, or one. */
function CardCsvExport({ cards, onClose }: { cards: CardSummary[]; onClose: () => void }) {
  const [cardId, setCardId] = useState('');
  return (
    <CsvExportDialog
      title="Esporta le spese delle carte in CSV"
      subtitle="Per il commercialista"
      path="/treasury/cards/movements/csv"
      extra={cardId ? { cardId } : {}}
      fields={
        <Field label="Carta">
          <select value={cardId} onChange={(event) => setCardId(event.target.value)}>
            <option value="">Tutte le carte</option>
            {cards.map((card) => (
              <option key={card.id} value={card.id}>
                •••• {card.last4} · {card.holder}
              </option>
            ))}
          </select>
        </Field>
      }
      note="Segno come in un conto corrente: le spese escono con il −, i rimborsi entrano con il +. Nella pagina restano come nell’estratto della carta."
      fallbackName="spese-carte.csv"
      onClose={onClose}
    />
  );
}
